import { Bell, Blocks, Code2, Key } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router'
import { cn } from '@/lib/utils'
import { APIKeysTab } from './integrations/APIKeysTab'
import { WebhooksTab } from './integrations/WebhooksTab'
import { WidgetEmbarqueTab } from './integrations/WidgetEmbarqueTab'

type Tab = 'keys' | 'webhooks' | 'widget'

export function IntegrationsView() {
  const { t } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()
  const integ = searchParams.get('integ')
  const activeTab: Tab = integ === 'webhooks' ? 'webhooks' : integ === 'widget' ? 'widget' : 'keys'

  const setActiveTab = (tab: Tab) => {
    const next = new URLSearchParams(searchParams)
    if (tab === 'keys') next.delete('integ')
    else next.set('integ', tab)
    setSearchParams(next)
  }

  const tabs: { id: Tab; label: string; icon: typeof Key }[] = [
    { id: 'keys', label: t('manager.integrations.tabs.keys'), icon: Key },
    { id: 'webhooks', label: t('manager.integrations.tabs.webhooks'), icon: Bell },
    { id: 'widget', label: t('manager.integrations.tabs.widget'), icon: Blocks },
  ]

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 overflow-y-auto">
        <div className="px-6 lg:px-8 py-6 space-y-6">
          {/* Header — page "Développeurs" (clés API + webhooks raw). Le titre
              et l'icône sont distincts de la marketplace 1-clic, mais le
              composant reste sur l'URL ?view=integrations pour préserver les
              bookmarks existants. */}
          <div>
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-blue-100 dark:bg-blue-500/20 rounded-xl flex items-center justify-center">
                <Code2 className="w-5 h-5 text-blue-600" />
              </div>
              <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">
                {t('manager.developers.sectionTitle')}
              </h1>
            </div>
            <p className="text-sm text-slate-500 dark:text-slate-400 ml-[52px]">
              {t('manager.developers.sectionSubtitle')}
            </p>
          </div>

          {/* Tab selector */}
          <div className="flex gap-1 bg-slate-100 dark:bg-slate-800 p-1 rounded-xl max-w-2xl">
            {tabs.map((tab) => {
              const Icon = tab.icon
              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={cn(
                    'flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-lg text-sm font-medium transition-all',
                    activeTab === tab.id
                      ? 'bg-white dark:bg-slate-700 text-slate-900 dark:text-white shadow-sm'
                      : 'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300',
                  )}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              )
            })}
          </div>

          {activeTab === 'keys' && <APIKeysTab />}
          {activeTab === 'webhooks' && <WebhooksTab />}
          {activeTab === 'widget' && <WidgetEmbarqueTab />}
        </div>
      </div>
    </div>
  )
}
